import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import {
  buildStatementSummary,
  downloadStatementCsv,
  downloadStatementExcel,
  downloadStatementPdf,
  filterStatementRows,
  formatStatementAmount,
  formatStatementDate,
  formatStatementPurpose,
  formatStatementReference,
  formatWalletName,
} from "../utils/statementUtils";

const API = "/backend/public/api";

const cardStyle = {
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "18px",
  padding: "16px 18px",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const inputStyle = {
  minWidth: "180px",
  borderRadius: "12px",
  border: "1px solid #cbd5e1",
  padding: "10px 12px",
  fontSize: "13px",
  color: "#334155",
  background: "#fff",
};

function AdminStatementPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ fromDate: "", toDate: "" });
  const exportRef = useRef(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin-statement`, {
        params: { _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => filterStatementRows(rows, filters), [rows, filters]);
  const summary = useMemo(() => {
    const totals = buildStatementSummary(filteredRows);
    return {
      ...totals,
      net: totals.totalCredit - totals.totalDebit,
    };
  }, [filteredRows]);

  const handleExportCsv = () =>
    downloadStatementCsv({
      fileName: "admin-statement.csv",
      rows: filteredRows,
      summary,
      includeCentre: true,
      filters,
    });

  const handleExportExcel = () =>
    downloadStatementExcel({
      fileName: "admin-statement.xls",
      rows: filteredRows,
      summary,
      includeCentre: true,
      filters,
    });

  const handleExportPdf = () =>
    downloadStatementPdf({
      fileName: "admin-statement.pdf",
      element: exportRef.current,
    });

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ fontSize: "13px", color: "#334155" }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h3 className="mb-1" style={{ fontSize: "24px" }}>View Statement</h3>
            <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
              Overall transaction history of every franchise wallet with cleaner wallet names, proper references, date filter, and export options.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={handleExportCsv} disabled={loading}>CSV</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleExportExcel} disabled={loading}>Excel</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleExportPdf} disabled={loading || !filteredRows.length}>PDF</button>
            <button className="btn btn-dark btn-sm" onClick={loadRows} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-end mb-4">
          <div>
            <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>From Date</label>
            <input type="date" style={inputStyle} value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
          </div>
          <div>
            <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>To Date</label>
            <input type="date" style={inputStyle} value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
          </div>
          <button className="btn btn-light btn-sm border" onClick={() => setFilters({ fromDate: "", toDate: "" })}>Clear Filter</button>
        </div>

        <div ref={exportRef}>
          <div
            className="mb-4"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "14px",
            }}
          >
            <div style={cardStyle}>
              <div className="text-muted fw-bold" style={{ fontSize: "12px" }}>Total Credit</div>
              <div className="fw-bold" style={{ fontSize: "26px", color: "#0f172a" }}>Rs {summary.totalCredit.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div className="text-muted fw-bold" style={{ fontSize: "12px" }}>Total Debit</div>
              <div className="fw-bold" style={{ fontSize: "26px", color: "#0f172a" }}>Rs {summary.totalDebit.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div className="text-muted fw-bold" style={{ fontSize: "12px" }}>Net Movement</div>
              <div className="fw-bold" style={{ fontSize: "26px", color: "#0f172a" }}>Rs {summary.net.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div className="text-muted fw-bold" style={{ fontSize: "12px" }}>Total Point Credit</div>
              <div className="fw-bold" style={{ fontSize: "26px", color: "#0f172a" }}>{summary.totalPointCredit.toFixed(2)} Pt</div>
            </div>
          </div>

          <div className="table-responsive bg-white shadow-sm rounded-4 p-2">
            <table className="table align-middle mb-0" style={{ fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Sl No</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Date</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Centre</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Wallet</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Type</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Amount</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Purpose</th>
                  <th style={{ fontSize: "12px", color: "#7c2d3a" }}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredRows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>{formatStatementDate(row.created_at)}</td>
                    <td>
                      <div className="fw-semibold">{row.centre_name || row.login_id || `Franchise ${row.franchise_id}`}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>{row.centre_code || row.login_id || "-"}</div>
                    </td>
                    <td>{formatWalletName(row.wallet_type)}</td>
                    <td className="text-capitalize fw-bold" style={{ color: row.transaction_type === "credit" ? "#166534" : "#b91c1c" }}>{row.transaction_type}</td>
                    <td>{formatStatementAmount(row.amount, row.wallet_type)}</td>
                    <td style={{ fontSize: "12px", lineHeight: 1.5 }}>{formatStatementPurpose(row)}</td>
                    <td>{formatStatementReference(row)}</td>
                  </tr>
                ))}
                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">No transaction found.</td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">Loading transaction statement...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 991px) {
          .container-fluid [style*="repeat(4, minmax(0, 1fr))"] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 575px) {
          .container-fluid [style*="repeat(4, minmax(0, 1fr))"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </DashboardLayout>
  );
}

export default AdminStatementPage;



